import Big from 'big.js';
import { LocalDate } from '@js-joda/core';

const SERVICE_FEE = Big(0);

const state = {
    cart: new Map(),
    bookingData: null,
    availabilities: [],
    lastFetchedDate: LocalDate.now(),
    date: null,
    time: null,
    appliedCoupon: null,
    availableTimes: [],
    regionCategorySlugs: {},
};

const mutations = {
    SET_BOOKING_DATA(state, data) {
        state.bookingData = data;
    },
    SET_AVAILABILITIES(state, availabilities) {
        if (availabilities.length) state.availabilities.push(...availabilities);
    },
    SET_LAST_FETCHED_DATE(state, date) {
        state.lastFetchedDate = date;
    },
    SET_REGION_CATEGORY(state, slugs) {
        state.regionCategorySlugs = slugs;
    },
    SET_DATE(state, date) {
        state.date = date;
    },
    SET_TIME(state, time) {
        state.time = time;
    },
    SET_AVAILABLE_TIMES(state, times) {
        state.availableTimes = times;

        // If the selected time is not in the available times, reset the selected time to null
        if (state.time && !state.availableTimes.includes(state.time)) {
            state.time = null;
        }
    },
    SET_OFFER(state, offer) {
        const quantity = state.cart.get(offer.id) || 0;
        state.cart.set(offer.id, quantity + 1);
    },
    REMOVE_OFFER(state, offer) {
        const quantity = state.cart.get(offer.id) || 0;

        if (quantity > 1) {
            state.cart.set(offer.id, quantity - 1);
        } else {
            state.cart.delete(offer.id);
        }
    },
    SET_CART_ITEM(state, item) {
        state.cart.set(item.id, item.quantity);
    },
};

const actions = {
    async fetchBookingData({ commit }, id) {
        return axios
            .get(`/booking/${id}`)
            .then((response) => {
                const data = response.data;
                commit('SET_BOOKING_DATA', data);
            })
            .catch((error) => {
                throw error;
            });
    },
    async postBookingData({ commit }, { id, data }) {
        return axios
            .post(`/booking/${id}`, data)
            .then((response) => {
                commit('SET_BOOKING_DATA', response.data);
            })
            .catch((error) => {
                throw error;
            });
    },
    async sendCustomer({ commit }, { id, customer }) {
        return axios
            .post(`/booking/${id}/customer`, customer)
            .then((response) => {
                commit('SET_BOOKING_DATA', response.data);
            })
            .catch((error) => {
                throw error;
            });
    },
    async fetchAvailabilities({ commit }, { serviceId, start, end }) {
        return axios
            .get(`/api/availability/${serviceId}?from=${start}&to=${end}`)
            .then((response) => {
                const availabilities = response.data;
                commit('SET_AVAILABILITIES', availabilities);
                commit('SET_LAST_FETCHED_DATE', end);
            })
            .catch((error) => {
                throw error;
            });
    },
    async populateCart({ commit, getters }) {
        const items = getters.getItems;
        for (const item of Object.values(items)) {
            commit('SET_CART_ITEM', item);
        }
    },
};
const getters = {
    getServiceId(state) {
        return state.bookingData?.service?.id ?? null;
    },
    getOnlinePaymentEligibility(state) {
        return state.bookingData?.service?.constraints?.isOnlinePaymentEligible ?? null;
    },
    getServiceTitle(state) {
        return state.bookingData?.service?.title ?? null;
    },
    getSelectedDate(state) {
        return state.bookingData?.date ?? null;
    },
    getSelectedTime(state) {
        return state?.bookingData?.time ?? null;
    },
    getExpiresAt(state) {
        return state?.bookingData?.expiresAt ?? null;
    },
    getAdvanceMinutes(state) {
        return state.bookingData?.service?.constraints?.advanceMinutes ?? null;
    },
    getAvailabilities(state) {
        return state?.availabilities ?? null;
    },
    getLastFetchedDate(state) {
        return state.lastFetchedDate;
    },
    getOffers(state) {
        return state.bookingData?.service?.availableOffers ?? null;
    },
    getOfferQuantity: (state) => (offer) => {
        return state.cart.get(offer.id) || 0;
    },
    getMinPeople(state) {
        return state.bookingData?.service?.constraints?.minPeople ?? null;
    },
    getMaxPeople(state) {
        return state.bookingData?.service?.constraints?.maxPeople ?? null;
    },
    getTime(state) {
        return state.time;
    },
    getDate(state) {
        return state.date;
    },
    getCart(state) {
        return state.cart;
    },
    getAvailableTimes(state) {
        return state.availableTimes;
    },
    getCustomer(state) {
        return state?.bookingData?.customer ?? null;
    },
    getCustomerCountry(state) {
        return state?.bookingData?.customer?.country?.country_code ?? null;
    },
    getTotalPeople(state, getters) {
        const offers = getters.getOffers;
        let sum = 0;
        state.cart.forEach((quantity, offerId) => {
            const offer = offers.find((offer) => offer.id === offerId);
            if (offer) {
                sum += quantity * Number(offer.group_num);
            } else {
                console.error(`Offer with ID ${offerId} not found.`);
            }
        });
        return sum;
    },
    getPeopleNum: (state, getters) => (offer) => {
        const offerQuantity = getters.getOfferQuantity(offer);
        return offerQuantity * offer.group_num;
    },
    getRegionCategorySlugs(state) {
        return state.regionCategorySlugs;
    },
    getServicePhoto(state) {
        return state?.bookingData?.service?.photo ?? null;
    },
    getPriceFormatted(state) {
        return state?.bookingData?.priceFormatted ?? null;
    },
    getSubTotalFormatted(state) {
        return state?.bookingData?.subTotalFormatted ?? null;
    },
    getTotalFormatted(state) {
        return state?.bookingData?.totalFormatted ?? null;
    },
    getItems(state) {
        return state.bookingData.items;
    },
    getAppliedCoupon(state) {
        return state.appliedCoupon;
    },
    getPriceSum(state, getters) {
        const offers = getters.getOffers;
        let sum = Big(0);
        state.cart.forEach((quantity, offerId) => {
            const offer = offers.find((offer) => offer.id === offerId);
            if (offer) {
                const calcPrice = Big(offer.discounted_price.amount).times(quantity);
                sum = sum.plus(calcPrice);
            } else {
                console.error(`Offer with ID ${offerId} not found.`);
            }
        });
        return sum.toString();
    },
    getTotalPrice(state, getters) {
        const priceSum = getters.getPriceSum;
        const appliedCoupon = state.appliedCoupon;
        const couponDiscount = getters.getCouponDiscount;
        let sum = Big(priceSum);

        if (appliedCoupon) {
            sum = sum.minus(couponDiscount);
        }
        return sum.toFixed(2);
    },
    getPlatformCost(state, getters) {
        const totalPrice = getters.getTotalPrice;
        return Big(totalPrice).times(SERVICE_FEE.div(100)).toFixed(2);
    },
    getTotalCost(state, getters) {
        const totalPrice = getters.getTotalPrice;
        const platformCost = getters.getPlatformCost;
        return Big(totalPrice).plus(platformCost).toFixed(2);
    },
    getCouponDiscount(state, getters) {
        const priceSum = getters.getPriceSum;
        const appliedCoupon = state.appliedCoupon;
        const ZERO = Big(0);

        if (appliedCoupon) {
            return Big(priceSum)
                .times(appliedCoupon.discount_percentage / 100)
                .toFixed(2);
        }

        return ZERO.toFixed(2);
    },
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
